import { clsx } from 'clsx';

export interface ProgressBarProps {
  value: number;
  max: number;
  label?: string;
  showValue?: boolean;
  accent?: 'primary' | 'success' | 'warn' | 'danger';
  size?: 'xs' | 'sm' | 'md';
  'aria-label'?: string;
}

// Solid backgrounds — gradient + opacity modifiers don't compute reliably
// against our CSS-variable colors, so the bars rendered transparent.
const ACCENT_BG: Record<string, string> = {
  primary: 'bg-primary',
  success: 'bg-success',
  warn: 'bg-warn',
  danger: 'bg-danger',
};

const HEIGHT: Record<string, string> = { xs: 'h-1.5', sm: 'h-2', md: 'h-2.5' };

export function ProgressBar({ value, max, label, showValue, accent = 'primary', size = 'sm', 'aria-label': ariaLabel }: ProgressBarProps) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div>
      {(label || showValue) && (
        <div className="mb-1 flex items-center justify-between text-2xs">
          {label && <span className="font-medium text-ink-muted">{label}</span>}
          {showValue && <span className="font-mono tabular-nums text-ink-dim">{pct.toFixed(0)}%</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={ariaLabel ?? label ?? 'Progress'}
        className={clsx('w-full overflow-hidden rounded-full bg-surface-3', HEIGHT[size])}
      >
        <div
          className={clsx('h-full rounded-full transition-all duration-700 ease-out origin-left animate-bar-grow', ACCENT_BG[accent])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
